import React from 'react';
import Table, { CellProps } from 'rsuite/Table';
import IconButton from 'rsuite/IconButton';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import { RouterOutput, trpcSwr } from '../../../../api/trpc';
import { fetcher } from '../../../../services/api';
import { WhisperSpeaker } from '../../../../utils/whisper-speaker';
import { ModalsService } from '../../../../services/modals.service';
import { AddRecommendationUtmModal } from './add-recommendation-utm-modal';
import MyLoader from '../../../common/loader.component';
import { AlertService } from '../../../../services/alert.service';
import { ToggleCell } from '../../../common/table/toggle-cell';

type Item = RouterOutput['recommendationUtmAdmin']['list']['items'][number];

export const RecommendationUtmsTab = () => {
  const {
    data: utmList,
    isLoading,
    error,
    mutate,
  } = trpcSwr.recommendationUtmAdmin.list.useSWR();
  const { trigger: triggerUpdate } =
    trpcSwr.recommendationUtmAdmin.update.useSWRMutation({
      fetcher,
    });
  const { trigger: triggerDelete } =
    trpcSwr.recommendationUtmAdmin.delete.useSWRMutation({
      fetcher,
    });

  const renderMenu =
    (item: Item): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 'edit') {
          ModalsService.createModal(AddRecommendationUtmModal, {
            item: {
              ...item,
              caption: item.caption || '',
              tags: item.tags.map((tag) => ({ value: tag })),
            },
          }).then(() => mutate());
        } else if (eventKey === 'delete') {
          triggerDelete({ id: item.id }).then(() => {
            mutate();
          });
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'edit'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'delete'}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({ rowData, dataKey, ...props }: CellProps<Item>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  if (isLoading) {
    return (
      <>
        <div className="pb-4">
          <MyLoader />
        </div>
      </>
    );
  }

  if (error || !utmList) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  return (
    <div>
      <Table
        loading={isLoading}
        data={utmList.items || []}
        headerHeight={57}
        rowHeight={70}
        autoHeight
        rowKey="id"
      >
        <Table.Column width={400} verticalAlign="middle" fullText>
          <Table.HeaderCell>Название utm</Table.HeaderCell>
          <Table.Cell>
            {(rowData) => {
              return (
                <>
                  utm_{rowData.caption}={rowData.value}{' '}
                  <div className={'mleft-auto'}>
                    <IconButton
                      circle={true}
                      appearance="subtle"
                      size={'lg'}
                      className="ms-4"
                      icon={<i className="bi bi-link" />}
                      onClick={() => {
                        navigator.clipboard
                          .writeText(`?utm_${rowData.caption}=${rowData.value}`)
                          .then(() => {
                            AlertService.success();
                          });
                      }}
                    />
                  </div>
                </>
              );
            }}
          </Table.Cell>
        </Table.Column>
        <Table.Column width={250} verticalAlign="middle" fullText>
          <Table.HeaderCell>Теги</Table.HeaderCell>
          <Table.Cell>{(rowData) => <>{rowData.tags.join(',')}</>}</Table.Cell>
        </Table.Column>
        <Table.Column width={100} verticalAlign="middle" fullText>
          <Table.HeaderCell>Включена</Table.HeaderCell>
          <ToggleCell
            dataKey="disabled"
            inverted={true}
            onChange={(rowData, checked) => {
              triggerUpdate({
                id: rowData.id,
                data: {
                  disabled: checked,
                },
              }).then(() => {
                mutate();
                AlertService.success();
              });
            }}
          />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>
    </div>
  );
};
